import React, { useState } from 'react';
import { Card, CardContent } from '../components/Card';
import { Button } from '../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Badge } from '../components/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/Tabs';
import { FileText, Link as LinkIcon, CheckCircle2, Edit, Trash2, BookOpen, Shield } from 'lucide-react';
const codingDocs = [{
  id: '1',
  name: 'ICD-10-CM Official Guidelines for Coding and Reporting',
  type: 'Standard',
  status: 'Ready',
  updated: '15/02/2026',
  by: 'System'
}, {
  id: '2',
  name: 'OPCS-4 Reference Manual (NHS)',
  type: 'Standard',
  status: 'Ready',
  updated: '10/01/2026',
  by: 'System'
}, {
  id: '3',
  name: 'WHO International Classification of Diseases',
  type: 'URL',
  status: 'Ready',
  updated: '05/03/2026',
  by: 'Clinical Coder'
}, {
  id: '4',
  name: 'Sravani Internal Coding Policies Q1 2026',
  type: 'File',
  status: 'Ready',
  updated: '01/03/2026',
  by: 'Lead Coder'
}, {
  id: '5',
  name: 'Cardiology Specific Coding Updates',
  type: 'File',
  status: 'Processing',
  updated: '12/03/2026',
  by: 'Clinical Coder'
}];
const insuranceDocs = [{
  id: '1',
  name: 'Bupa Pre-authorisation Rules 2026',
  type: 'File',
  status: 'Ready',
  updated: '20/02/2026',
  by: 'Billing Admin'
}, {
  id: '2',
  name: 'AXA Health Dispute Resolution Guidelines',
  type: 'URL',
  status: 'Ready',
  updated: '18/02/2026',
  by: 'Billing Admin'
}, {
  id: '3',
  name: 'Aviva Claims Submission Protocol',
  type: 'Standard',
  status: 'Ready',
  updated: '15/01/2026',
  by: 'System'
}, {
  id: '4',
  name: 'Vitality Coverage Exclusions List',
  type: 'File',
  status: 'Ready',
  updated: '05/03/2026',
  by: 'Billing Admin'
}];
const getTypeBadgeClass = (type: string) => {
  switch (type) {
    case 'Standard':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'File':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'URL':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    default:
      return '';
  }
};
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Ready':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Processing':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    default:
      return '';
  }
};
export function KnowledgeBase() {
  const [activeTab, setActiveTab] = useState('coding');
  return <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-4" data-id="element-712">
      <div className="flex justify-between items-start" data-id="element-713">
        <div data-id="element-714">
          <h1 className="font-heading text-2xl sm:text-3xl font-normal text-foreground mb-1" data-id="element-715">
            Knowledge Base
          </h1>
          <p className="text-muted-foreground text-sm" data-id="element-716">
            Manage sources your agent uses for coding and insurance decisions
          </p>
        </div>
        <div className="flex gap-3" data-id="element-717">
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-718">
            <FileText className="w-4 h-4" data-id="element-719" />
            Add File
          </Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-720">
            <LinkIcon className="w-4 h-4" data-id="element-721" />
            Add URL
          </Button>
        </div>
      </div>

      <Tabs defaultValue="coding" value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="element-722">
        <TabsList className="w-full max-w-md grid grid-cols-2 mb-6 bg-secondary" data-id="element-723">
          <TabsTrigger value="coding" className="flex items-center gap-2 data-[state=active]:!bg-primary data-[state=active]:!text-primary-foreground data-[state=active]:!shadow-sm" data-id="element-724">
            <BookOpen className="w-4 h-4" data-id="element-725" />
            Coding Protocols
          </TabsTrigger>
          <TabsTrigger value="insurance" className="flex items-center gap-2 data-[state=active]:!bg-primary data-[state=active]:!text-primary-foreground data-[state=active]:!shadow-sm" data-id="element-726">
            <Shield className="w-4 h-4" data-id="element-727" />
            Insurance Protocols
          </TabsTrigger>
        </TabsList>

        <TabsContent value="coding" data-id="element-728">
          <Card data-id="element-729">
            <Table data-id="element-730">
              <TableHeader data-id="element-731">
                <TableRow data-id="element-732">
                  <TableHead data-id="element-733">Source Name</TableHead>
                  <TableHead data-id="element-734">Type</TableHead>
                  <TableHead data-id="element-735">Status</TableHead>
                  <TableHead data-id="element-736">Last Updated</TableHead>
                  <TableHead data-id="element-737">Updated By</TableHead>
                  <TableHead className="text-right" data-id="element-738">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-739">
                {codingDocs.map(doc => <TableRow key={doc.id} className="hover:bg-secondary/50" data-id="element-740">
                    <TableCell className="font-medium text-foreground" data-id="element-741">
                      {doc.name}
                    </TableCell>
                    <TableCell data-id="element-742">
                      <Badge variant="outline" className={getTypeBadgeClass(doc.type)} data-id="element-743">
                        {doc.type}
                      </Badge>
                    </TableCell>
                    <TableCell data-id="element-744">
                      <div className="flex items-center gap-2" data-id="element-745">
                        {doc.status === 'Ready' && <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-746" />}
                        <Badge variant="outline" className={getStatusBadgeClass(doc.status)} data-id="element-747">
                          {doc.status}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-748">
                      {doc.updated}
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-749">
                      {doc.by}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-750">
                      <div className="flex justify-end gap-2" data-id="element-751">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-752">
                          <Edit className="w-4 h-4" data-id="element-753" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-id="element-754">
                          <Trash2 className="w-4 h-4" data-id="element-755" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="insurance" data-id="element-756">
          <Card data-id="element-757">
            <Table data-id="element-758">
              <TableHeader data-id="element-759">
                <TableRow data-id="element-760">
                  <TableHead data-id="element-761">Source Name</TableHead>
                  <TableHead data-id="element-762">Type</TableHead>
                  <TableHead data-id="element-763">Status</TableHead>
                  <TableHead data-id="element-764">Last Updated</TableHead>
                  <TableHead data-id="element-765">Updated By</TableHead>
                  <TableHead className="text-right" data-id="element-766">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-767">
                {insuranceDocs.map(doc => <TableRow key={doc.id} className="hover:bg-secondary/50" data-id="element-768">
                    <TableCell className="font-medium text-foreground" data-id="element-769">
                      {doc.name}
                    </TableCell>
                    <TableCell data-id="element-770">
                      <Badge variant="outline" className={getTypeBadgeClass(doc.type)} data-id="element-771">
                        {doc.type}
                      </Badge>
                    </TableCell>
                    <TableCell data-id="element-772">
                      <div className="flex items-center gap-2" data-id="element-773">
                        {doc.status === 'Ready' && <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-774" />}
                        <Badge variant="outline" className={getStatusBadgeClass(doc.status)} data-id="element-775">
                          {doc.status}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-776">
                      {doc.updated}
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-777">
                      {doc.by}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-778">
                      <div className="flex justify-end gap-2" data-id="element-779">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-780">
                          <Edit className="w-4 h-4" data-id="element-781" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-id="element-782">
                          <Trash2 className="w-4 h-4" data-id="element-783" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>;
}